import React, { useCallback, useMemo, useRef } from 'react'

import { HiOutlineFunnel, HiOutlinePaperAirplane } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import Badge from '@/components/common/Badge'
import Button from '@/components/common/Button'
import CuiFieldNotice from '@/components/common/CuiFieldNotice'
import SearchBar from '@/components/common/SearchBar'
import ToolbarContainer from '@/components/common/Toolbar'
import { useSearchContext } from '@/components/search/context'
import Settings, {
  type Settings as SettingsType,
} from '@/components/search/Settings'

import {
  calcNumDiffDibbsContractSearchFilter,
  calcNumDiffFederalContractSearchFilter,
  calcNumDiffFederalGrantSearchFilter,
  calcNumDiffSbirSearchFilter,
  calcNumDiffSledContractSearchFilter,
} from '@/helpers/search/filter'

import { useMounted } from '@/hooks/common/useMounted'
import { useActiveOrgHasCuiEnabled } from '@/hooks/organization/useActiveOrg'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

interface Toolbar {
  placeholder: string
}

const Toolbar: React.FC<Toolbar> = ({ placeholder }) => {
  const {
    autoSubmit,
    filter,
    filterDrawerIsOpen,
    query,
    type,
    useFullTextSearch,
    setAutoSubmit,
    setFilterDrawerIsOpen,
    setQuery,
    setRequest,
    setUseFullTextSearch,
  } = useSearchContext()
  const { accessToken } = useAuthInfo()
  const { activeOrgId } = useActiveOrgId()
  const { data: activeOrgHasCuiEnabled } = useActiveOrgHasCuiEnabled()

  const searchBarRef = useRef<HTMLInputElement>(null)

  const isMounted = useMounted()

  const settings: SettingsType = useMemo(() => {
    return { autoSubmit, useFullTextSearch }
  }, [autoSubmit, useFullTextSearch])

  const setSettings = useCallback(
    (settings: SettingsType) => {
      setAutoSubmit(settings.autoSubmit)
      setUseFullTextSearch(settings.useFullTextSearch)
    },
    [setAutoSubmit, setUseFullTextSearch],
  )

  const filterNumDiff = useMemo(() => {
    const type = filter?.type
    if (!type) {
      return 0
    }
    switch (type) {
      case 'dibbs_contract':
        return calcNumDiffDibbsContractSearchFilter(filter.filter)
      case 'federal_contract':
        return calcNumDiffFederalContractSearchFilter(filter.filter)
      case 'federal_grant':
        return calcNumDiffFederalGrantSearchFilter(filter.filter)
      case 'sbir':
        return calcNumDiffSbirSearchFilter(filter.filter)
      case 'sled_contract':
        return calcNumDiffSledContractSearchFilter(filter.filter)
      default:
        const _exhaustiveCheck: never = type
        throw new Error(`Unhandled filter type: ${_exhaustiveCheck}`)
    }
  }, [filter])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'l' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      searchBarRef.current?.focus()
    } else if (event.key === 'Enter') {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }

  const handleSubmit = () => {
    const type = filter?.type
    if (!type) {
      return
    }
    switch (type) {
      case 'dibbs_contract':
        setRequest(
          {
            query: query.trim(),
            organization_id: activeOrgId,
            type: 'dibbs_contract',
            filter: {
              type: 'dibbs_contract',
              filter: filter.filter,
            },
            use_fts: useFullTextSearch,
          },
          accessToken,
        )
        break
      case 'federal_contract':
        setRequest(
          {
            query: query.trim(),
            organization_id: activeOrgId,
            type: 'federal_contract',
            filter: {
              type: 'federal_contract',
              filter: filter.filter,
            },
            use_fts: useFullTextSearch,
          },
          accessToken,
        )
        break
      case 'federal_grant':
        setRequest(
          {
            query: query.trim(),
            organization_id: activeOrgId,
            type: 'federal_grant',
            filter: {
              type: 'federal_grant',
              filter: filter.filter,
            },
            use_fts: useFullTextSearch,
          },
          accessToken,
        )
        break
      case 'sbir':
        setRequest(
          {
            query: query.trim(),
            organization_id: activeOrgId,
            type: 'sbir',
            filter: {
              type: 'sbir',
              filter: filter.filter,
            },
            use_fts: useFullTextSearch,
          },
          accessToken,
        )
        break
      case 'sled_contract':
        setRequest(
          {
            query: query.trim(),
            organization_id: activeOrgId,
            type: 'sled_contract',
            filter: {
              type: 'sled_contract',
              filter: filter.filter,
            },
            use_fts: useFullTextSearch,
          },
          accessToken,
        )
        break
      default:
        const _exhaustiveCheck: never = type
        throw new Error(`Unhandled filter type: ${_exhaustiveCheck}`)
    }
  }

  const setValue = (value: string) => {
    setQuery(value)
  }

  const toggleFilterDrawer = () => {
    setFilterDrawerIsOpen(!filterDrawerIsOpen)
  }

  const showCuiQueryNotice =
    activeOrgHasCuiEnabled === true &&
    (type === 'federal_contract' || type === 'sled_contract')

  return (
    <ToolbarContainer className="!border-0">
      <div className="flex flex-col md:flex-row w-full max-w-screen-3xl gap-x-2 gap-y-2">
        <div className="relative flex flex-col-reverse md:flex-row w-full justify-start gap-x-2 gap-y-4 py-1 py-3">
          <div className="w-full flex gap-x-2">
            <Settings settings={settings} setSettings={setSettings} />
            <div className="flex lg:hidden items-center">
              <Button variant="outline" onClick={toggleFilterDrawer}>
                <HiOutlineFunnel className="w-5 h-5 text-primary-600 dark:text-primary-300" />
                {isMounted && filterNumDiff > 0 && (
                  <Badge count={filterNumDiff} />
                )}
              </Button>
            </div>
            <SearchBar
              ref={searchBarRef}
              value={isMounted && query ? query : ''} // this is needed because of some weird SSR thing, not sure why
              onChange={handleQueryChange}
              onKeyDown={handleKeyDown}
              setValue={setValue}
              placeholder={isMounted ? placeholder : ''}
              containerClassName="w-full h-full shadow-none"
              className="text-sm"
              maxLength={256}
              rightAdornment={
                showCuiQueryNotice ? (
                  <CuiFieldNotice variant="icon" side="bottom">
                    Refrain from including CUI in search queries.
                  </CuiFieldNotice>
                ) : undefined
              }
            />
            <div className={twMerge('flex items-center')}>
              <Button className="px-2" onClick={handleSubmit}>
                <HiOutlinePaperAirplane className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ToolbarContainer>
  )
}

export default Toolbar
